import { FC, useMemo } from 'react';

import { EvSection } from '@evinced-private/ui-common';

import {
	MFA_SESSION_SCANS_TAB_DAID,
	MFA_SESSION_SCANS_TABLE_DAID
} from 'data-automation-ids/MfaDashboardAutomationIds';
import {
	MobileErrorMessage,
	MobileErrorType
} from 'src/apps/mobile-common/components/mobile-error-message/MobileErrorMessage';
import { useMobileScansListQuery } from 'src/apps/mobile-common/hooks/useMobileScansListQuery';
import { PlatformTable } from 'src/common/components/platform-table/PlatformTable';
import { buildFullTableId } from 'src/common/helpers/TableStateHelper';
import { ProductType } from 'src/common/interfaces/Tenant';
import { MFA_SESSION_SCANS_TABLE_ID } from 'src/common/pages/dashboards-container/dashboard_consts';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';

import { MFA_SESSION_SCANS_LABELS } from './MfaSessionScans.labels';
import { IMfaSessionScansProps } from './MfaSessionScans.types';
import { getMfaSessionScansColumns } from './MfaSessionScansTableHelper';

import './MfaSessionScans.scss';

const EMPTY_SUMMARY = {
	scansCount: 0,
	issuesCount: 0,
	elementsWithIssuesCount: 0
};

export const MfaSessionScans: FC<IMfaSessionScansProps> = ({ sessionId }) => {
	const { getTableStateById } = useTableState();

	const tableId = useMemo(
		() => buildFullTableId(MFA_SESSION_SCANS_TABLE_ID, sessionId),
		[sessionId]
	);
	const tableState = getTableStateById(tableId);
	const { tableSort, paginationInfo } = tableState;

	const scansQuery = useMobileScansListQuery({
		runId: sessionId,
		productName: ProductType.MOBILE_FLOW_ANALYZER,
		sort: tableSort?.[0],
		page: paginationInfo.page,
		pageSize: paginationInfo.sizePerPage
	});

	const columns = useMemo(() => getMfaSessionScansColumns(), []);

	if (scansQuery.isError) {
		return (
			<MobileErrorMessage
				errorType={MobileErrorType.LOADING_ERROR}
				productType={ProductType.MOBILE_FLOW_ANALYZER}
			/>
		);
	}

	const { rows = [], total = 0, summary = EMPTY_SUMMARY } = scansQuery.data ?? {};
	const isTableLoading = scansQuery.isLoading || scansQuery.isPreviousData;

	return (
		<EvSection
			className="mfa-session-scans"
			dataAutomationId={MFA_SESSION_SCANS_TAB_DAID}
			ariaLabel={MFA_SESSION_SCANS_LABELS.SECTION_ARIA_LABEL}
		>
			<EvSection ariaLabel={MFA_SESSION_SCANS_LABELS.TABLE_ARIA_LABEL} skipLinkId="main-section">
				<PlatformTable
					dataAutomationId={MFA_SESSION_SCANS_TABLE_DAID}
					isTableLoading={isTableLoading}
					tableId={tableId}
					tableData={rows}
					totalCount={total}
					totalPaginationableResults={total}
					className="mfa-session-scans__table"
					persistTableState
					columns={columns}
					options={{
						keyField: 'scanId',
						title: MFA_SESSION_SCANS_LABELS.TABLE_TITLE_WITH_SUMMARY(summary),
						caption: MFA_SESSION_SCANS_LABELS.TABLE_CAPTION,
						pagination: true,
						dataType: MFA_SESSION_SCANS_LABELS.SCANS_DATA_TYPE_WITH_SUMMARY(summary),
						remote: true
					}}
					withUrlSwitcher={false}
				/>
			</EvSection>
		</EvSection>
	);
};
